import { FileText, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import type { SearchResult } from "../types";

interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

export function SearchDialog({ open, onClose, onNavigate }: SearchDialogProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [includeLogs, setIncludeLogs] = useState(false);
  const [selected, setSelected] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 0);
    document.body.dataset.dialogOpen = "true";
    return () => { delete document.body.dataset.dialogOpen; };
  }, [open]);

  useEffect(() => {
    if (!open || !query.trim()) {
      setResults([]);
      setSelected(0);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.search(query, includeLogs);
        if (!controller.signal.aborted) {
          setResults(response.results);
          setSelected(0);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 120);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [open, query, includeLogs]);

  if (!open) return null;
  const choose = (path: string) => {
    onNavigate(path);
    onClose();
    setQuery("");
  };

  return (
    <div className="search-dialog-shell" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section
        className="search-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="搜索知识库"
        onKeyDown={(event) => {
          if (event.key === "Escape") onClose();
          if (event.key === "ArrowDown") { event.preventDefault(); setSelected((value) => Math.min(results.length - 1, value + 1)); }
          if (event.key === "ArrowUp") { event.preventDefault(); setSelected((value) => Math.max(0, value - 1)); }
          if (event.key === "Enter" && results[selected]) choose(results[selected].path);
        }}
      >
        <header className="search-dialog-head">
          <Search size={20} />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="搜索标题、正文、标签或路径…"
            autoComplete="off"
          />
          {loading && <span className="search-loading" />}
          <button type="button" className="icon-button" onClick={onClose} aria-label="关闭搜索"><X size={20} /></button>
        </header>
        <div className="search-options">
          <label><input type="checkbox" checked={includeLogs} onChange={(event) => setIncludeLogs(event.target.checked)} /> 包含低信号操作日志</label>
          <span>{results.length ? `${results.length} 个结果` : "⌘K 随时打开"}</span>
        </div>
        <div className="search-results" role="listbox">
          {!query.trim() && (
            <div className="search-empty">
              <strong>直接找判断，不必翻目录。</strong>
              <span>中文正文、标题、标签和英文路径会一起参与检索。</span>
            </div>
          )}
          {query.trim() && !loading && !results.length && <div className="search-empty"><strong>没有命中。</strong><span>换一个更短的关键词试试。</span></div>}
          {results.map((result, index) => (
            <button
              type="button"
              role="option"
              aria-selected={selected === index}
              data-selected={selected === index || undefined}
              key={result.path}
              onMouseEnter={() => setSelected(index)}
              onClick={() => choose(result.path)}
            >
              <FileText size={17} />
              <span className="search-result-copy">
                <strong>{result.title}</strong>
                <small>{result.excerpt}</small>
              </span>
              <code>{result.path}</code>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
